import cn from 'classnames';
import { Tooltip } from '#components/atoms/tooltip/Tooltip.tsx';
import type { EndpointOIDCStatus } from '#dn-types/endpoint-oidc.ts';
import type { HostStatus } from '#dn-types/host.ts';
import type { ManagedLighthouse } from '#dn-types/managed-lighthouse.ts';
import styles from './StatusDot.module.css';

/** Managed-lighthouse light: the tenancy statuses plus 'off' for a network that opted out. */
type ManagedLighthouseDotStatus = ManagedLighthouse['status'] | 'off';

type Props = {
  className?: string;
  status: HostStatus | EndpointOIDCStatus | ManagedLighthouseDotStatus;
  /**
   * Render a bare, aria-hidden dot with no tooltip or screen-reader text. For call sites that put
   * a visible status label right next to the dot — the default announces/tooltips the status a
   * second time.
   */
  decorative?: boolean;
};

export function StatusDot({ className, status, decorative }: Props) {
  const classes = cn(
    styles.StatusDot,
    {
      [styles.StatusDot___green]: status === 'online' || status === 'enabled' || status === 'active',
      [styles.StatusDot___filled]:
        status === 'inactive' || status === 'disabled' || status === 'pending' || status === 'off',
      [styles.StatusDot___open]:
        status === 'unknown' ||
        status === 'unenrolled' ||
        status === 'blocked' ||
        status === 'no domain' ||
        status === 'pending verification',
    },
    className
  );

  if (decorative) {
    return <span aria-hidden={true} className={classes} />;
  }

  return (
    <Tooltip content={<span className={styles.StatusDot_tooltip}>{status}</span>} className={classes}>
      <span className="visually-hidden">{status} </span>
    </Tooltip>
  );
}
